import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { findActualColumnInfo } from '../utils';
import { convertToPt, convertToPx } from '../../extension-word/utils';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/core';

interface ResizeState {
    dragging: boolean;
    startX: number;
    startTableWidth: number;
    movedX: number;
    activeCellPos: number | null;
}

const resizePluginKey = new PluginKey<ResizeState>('tableCellResizeHandle');

function updateTableCellWidths(view: EditorView, cellPos: number, tr: Transaction) {
    let tableNode: ProseMirrorNode | null = null;
    let tablePos: number | null = null;
    view.state.doc.nodesBetween(cellPos, cellPos + 1, (node: ProseMirrorNode, pos: number) => {
        if (node.type.name === 'table') {
            tableNode = node;
            tablePos = pos;
        }
    });

    if (tableNode && tablePos) {
        const nodeSize = (tableNode as ProseMirrorNode).nodeSize;
        view.state.doc.nodesBetween(tablePos, tablePos + nodeSize, (node: ProseMirrorNode, pos: number) => {
            if (node.type.name === 'table') {
                const tableDOM = view.nodeDOM(pos) as HTMLTableElement;
                const tableComputedStyle = getComputedStyle(tableDOM);
                const tableWidthPt = convertToPt(tableComputedStyle.width);
                if (node.attrs.width?.type === 'PERCENT') {
                    const tableParentWidthPt = tableDOM.parentElement ? convertToPt(getComputedStyle(tableDOM.parentElement).width) : tableWidthPt;
                    const tableWidthPct = (tableWidthPt / tableParentWidthPt) * 100;
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        width: { value: tableWidthPct, type: 'PERCENT' },
                        allowAutoFit: false,
                    });
                } else {
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        width: { value: tableWidthPt, type: 'POINTS' },
                        allowAutoFit: false,
                    });
                }
            } else if (node.type.name === 'tableCell') {
                const cellDOM = view.nodeDOM(pos) as HTMLElement;
                const cellComputedStyle = getComputedStyle(cellDOM);
                if (Math.abs(convertToPx(cellComputedStyle.width) - convertToPx(`${node.attrs.cellWidth?.value || 0}pt`)) < 1) return; // 1px 이하의 차이는 무시
                const cellWidth = convertToPt(cellComputedStyle.width);
                tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    cellWidth: { value: cellWidth, type: 'POINTS' },
                });
            }
        });
    }
}

export function tableResizePlugin(editor: Editor) {
    return new Plugin<ResizeState>({
        key: resizePluginKey,
        state: {
            init: () => ({
                dragging: false,
                startX: 0,
                startTableWidth: 0,
                movedX: 0,
                activeCellPos: null,
            }),
            apply(tr, state) {
                const meta = tr.getMeta(resizePluginKey);
                return meta ? { ...state, ...meta } : state;
            },
        },
        props: {
            handleDOMEvents: {
                mousemove(view, event: MouseEvent) {
                    if (!view.editable) return false;

                    const state = resizePluginKey.getState(view.state);
                    if (!state?.dragging || state.activeCellPos === null) return false;

                    const tr = view.state.tr;
                    const movedX = event.clientX - state.startX;

                    // dom의 colgroup에서 cell 과 같은 열에 위치하는 col 을 찾음
                    const cell = view.nodeDOM(state.activeCellPos) as HTMLElement;
                    const {
                        targetCell,
                        tableStructure: { colgroup },
                    } = findActualColumnInfo(view.state.doc, state.activeCellPos);
                    const actualColumnIndex = targetCell!.colEnd as number;
                    const tableElement = cell.closest('table') as HTMLTableElement;
                    const colElements = tableElement.getElementsByTagName('colgroup')[0].getElementsByTagName('col');
                    const colElement = colElements?.[actualColumnIndex] as HTMLTableColElement;
                    const changedWidth = convertToPt(colgroup[actualColumnIndex][1].width || '0pt') + convertToPt(movedX + 'px') + 'pt';
                    const newTableWidth = state.startTableWidth + movedX + 'px';
                    tableElement.style.minWidth = newTableWidth;
                    tableElement.style.width = newTableWidth;
                    tableElement.style.maxWidth = newTableWidth;
                    colElement.style.width = changedWidth;
                    colElement.style.minWidth = changedWidth;
                    colElement.style.maxWidth = changedWidth;

                    tr.setMeta(resizePluginKey, { movedX: event.clientX - state.startX });
                    view.dispatch(tr);
                    return true;
                },
                mouseup(view) {
                    if (!view.editable) return false;

                    const state = resizePluginKey.getState(view.state);
                    if (!state?.dragging || state.activeCellPos === null) return false;

                    const tr = view.state.tr;
                    updateTableCellWidths(view, state.activeCellPos, tr);

                    tr.setMeta(resizePluginKey, {
                        dragging: false,
                        activeCellPos: null,
                    });

                    view.dispatch(tr);
                    return true;
                },
                mousedown(view, event: MouseEvent) {
                    if (!view.editable) return false;

                    const target = event.target as HTMLElement;
                    if (!target.classList.contains('resize-handle')) return false;

                    const cell = target.closest('td');
                    if (!cell) return false;

                    const cellPos = view.posAtDOM(cell, 0) - 1;
                    const tr = view.state.tr;
                    updateTableCellWidths(view, cellPos, tr);

                    const tableElement = target.closest('table') as HTMLTableElement;
                    const startTableWidth = convertToPx(getComputedStyle(tableElement).width);
                    view.dispatch(
                        tr.setMeta(resizePluginKey, {
                            dragging: true,
                            startX: event.clientX,
                            startTableWidth,
                            movedX: 0,
                            activeCellPos: cellPos,
                        }),
                    );
                    event.preventDefault();
                    return true;
                },
            },
            decorations(state) {
                // editorable일때만 deco를 만들도록
                if (!editor.isEditable) return null;
                const decorations: Decoration[] = [];

                state.doc.descendants((node, pos) => {
                    if (node.type.name === 'tableCell') {
                        const deco = Decoration.widget(pos + 1, () => {
                            const handle = document.createElement('div');
                            handle.className = 'resize-handle';
                            handle.style.position = 'absolute';
                            handle.style.top = '0';
                            handle.style.right = '-2px';
                            handle.style.width = '4px';
                            handle.style.height = '100%';
                            handle.style.cursor = 'col-resize';
                            handle.style.userSelect = 'none';
                            handle.style.zIndex = '10';
                            return handle;
                        });

                        decorations.push(deco);
                    }
                });

                return DecorationSet.create(state.doc, decorations);
            },
        },
    });
}
